<script>
  // Multi-step enrollment, entirely client-side until the final submit:
  //   intro   → an "Enroll in the study" button
  //   form    → contact, demographics, languages, parts, prior study — all on
  //             one screen, "Review Information"
  //   review  → read-only summary (generated here, no server round-trip) and
  //             the consent statement together, "Complete Enrollment" (the
  //             only POST)
  import { postJSON } from '../lib/csrf.js'

  let {
    languages = [], // [{ key, label }] — key is the column prefix, label is shown
    proficiencies = [],
    nativeProficiency = '',
    doNotSpeak = 'Do Not Speak',
    sexes = [],
    yesNoResponses = [],
    priorStudies = [],
    years = [],
    minPasswordLength = 8,
    submitPath = '/enrollments',
  } = $props()

  let phase = $state('intro') // intro | form | review
  let errors = $state([]) // client-side validation messages for the current step
  let serverErrors = $state([])
  let submitting = $state(false)

  // Form fields
  let firstName = $state('')
  let lastName = $state('')
  let email = $state('')
  let password = $state('')
  let passwordConfirmation = $state('')
  let yearOfBirth = $state('')
  let sex = $state('')
  // Keyed by language key. Absent or doNotSpeak = not spoken.
  let proficiency = $state({})
  let childhood = $state({})
  let primaryLanguage = $state('')
  let part1 = $state(false)
  let part2 = $state(false)
  let lingos = $state('')
  let priorStudy = $state('')

  const spoken = (key) => proficiency[key] && proficiency[key] !== doNotSpeak
  const spokenLanguages = $derived(languages.filter((l) => spoken(l.key)))
  const nativeLanguages = $derived(
    spokenLanguages.filter((l) => proficiency[l.key] === nativeProficiency)
  )
  const participatingParts = $derived(
    [part1 ? 'Part 1' : null, part2 ? 'Part 2' : null].filter(Boolean)
  )

  function setProficiency(key, level) {
    proficiency[key] = level
    // Dropping a language back to "Do Not Speak" retires its childhood answer
    // and, if it was the primary language, that selection too.
    if (level === doNotSpeak) {
      delete childhood[key]
      if (primaryLanguage === key) primaryLanguage = ''
    }
    enforceNativeInvariant()
  }

  // A level between "Do Not Speak" and C2 may only be chosen while some *other*
  // language is already C2, which is what makes an all-below-C2 selection
  // unreachable: the option is disabled rather than accepting the choice and
  // rejecting it afterwards.
  function levelDisabled(key, level) {
    if (level === doNotSpeak || level === nativeProficiency) return false
    if (proficiency[key] === level) return false
    return !nativeLanguages.some((l) => l.key !== key)
  }

  // The one way around the disabled options is to lower the language that was
  // propping the others up, so whenever a change leaves nothing at C2, the
  // remaining levels are reset for re-selection.
  function enforceNativeInvariant() {
    if (spokenLanguages.length === 0 || nativeLanguages.length > 0) return
    for (const language of spokenLanguages) {
      proficiency[language.key] = doNotSpeak
      delete childhood[language.key]
    }
    primaryLanguage = ''
  }

  let scrollAnchor = $state(null)

  function scrollToTop() {
    window.scrollTo({ top: scrollAnchor?.offsetTop ?? 0, behavior: 'smooth' })
  }

  function go(next) {
    errors = []
    serverErrors = []
    phase = next
    scrollToTop()
  }

  function validateForm() {
    const e = []
    if (!firstName.trim()) e.push('First (given) name is required.')
    if (!lastName.trim()) e.push('Last (family) name is required.')
    if (!email.trim()) e.push('Email address is required.')
    if (!password) e.push('Password is required.')
    else if (password.length < minPasswordLength)
      e.push(`Password must be at least ${minPasswordLength} characters.`)
    else if (password !== passwordConfirmation) e.push('Password confirmation does not match.')
    if (!yearOfBirth) e.push('Year of birth is required.')
    if (!sex) e.push('Sex assigned at birth is required.')
    if (spokenLanguages.length === 0) e.push('Select a fluency level for at least one language.')
    else if (nativeLanguages.length === 0)
      e.push(`At least one of your languages must be “${nativeProficiency}”.`)
    else if (spokenLanguages.some((l) => !childhood[l.key]))
      e.push('Answer the childhood question for each language you speak.')
    if (!primaryLanguage) e.push('Select your primary language.')
    if (!part1 && !part2) e.push('Select at least one part to participate in.')
    if (!lingos) e.push('Please answer the LINGOS participation question.')
    if (!priorStudy) e.push('Please answer the prior-collection participation question.')
    errors = e
    return e.length === 0
  }

  function toReview() {
    // A failed validation still needs to scroll: the error list renders at the
    // top of this component, which — now that contact + details share one long
    // page below the study-overview component — can be thousands of pixels
    // above wherever the user was when they clicked.
    if (validateForm()) go('review')
    else scrollToTop()
  }

  function toFormFromIntro() {
    go('form')
  }

  async function complete() {
    submitting = true
    serverErrors = []
    const payload = {
      enrollment: {
        first_name: firstName,
        last_name: lastName,
        email_address: email,
        year_of_birth: yearOfBirth,
        sex_assigned_at_birth: sex,
        ...Object.fromEntries(
          languages.flatMap((l) => [
            [`${l.key}_proficiency`, proficiency[l.key] || ''],
            [`${l.key}_childhood`, childhood[l.key] || ''],
          ])
        ),
        primary_language: primaryLanguage,
        participate_part1: part1,
        participate_part2: part2,
        lingos_participation: lingos,
        prior_study_response: priorStudy,
      },
      user: {
        password: password,
        password_confirmation: passwordConfirmation,
      },
    }
    try {
      const res = await postJSON(submitPath, payload)
      const data = await res.json().catch(() => ({}))
      if (res.ok) {
        window.location.assign(data.redirect_url || '/')
      } else {
        serverErrors = data.errors || ['Something went wrong. Please try again.']
        submitting = false
      }
    } catch (err) {
      serverErrors = ['Network error. Please try again.']
      submitting = false
    }
  }

  const btnPrimary =
    'rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-white hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed'
  const btnSecondary =
    'rounded-md border border-gray-300 bg-white px-4 py-2 text-gray-700 hover:bg-gray-50'
  const field = 'mt-1 border rounded w-full'
</script>

<div bind:this={scrollAnchor} class="scroll-mt-8">
  {#if errors.length}
    <div class="mb-4 rounded-md bg-red-50 p-3 text-red-800">
      <ul class="list-disc list-inside">
        {#each errors as message}<li>{message}</li>{/each}
      </ul>
    </div>
  {/if}
  {#if serverErrors.length}
    <div class="mb-4 rounded-md bg-red-50 p-3 text-red-800">
      <ul class="list-disc list-inside">
        {#each serverErrors as message}<li>{message}</li>{/each}
      </ul>
    </div>
  {/if}

  {#if phase === 'intro'}
    <div class="text-center">
      <button type="button" class={btnPrimary + ' px-6'} onclick={toFormFromIntro}>
        Enroll in the study
      </button>
    </div>

  {:else if phase === 'form'}
    <p class="text-gray-700">You must be at least 18 years old and meet the language requirements of the study.</p>

    <fieldset class="mt-6 mb-8">
      <legend class="text-xl font-semibold text-gray-900">Contact Information</legend>
      <p class="mt-1 text-sm text-gray-600">
        The following information will be used to contact you about the study and to compensate you
        for your time.
      </p>

      <div class="mt-4">
        <span class="block font-medium text-gray-800">Name</span>
        <label class="mt-2 block text-sm text-gray-700">
          <span class="block"><span class="text-red-600">*</span> First (given) name</span>
          <input type="text" class={field} autocomplete="given-name" bind:value={firstName} />
        </label>
        <label class="mt-3 block text-sm text-gray-700">
          <span class="block"><span class="text-red-600">*</span> Last (family) name</span>
          <input type="text" class={field} autocomplete="family-name" bind:value={lastName} />
        </label>
      </div>

      <label class="mt-5 block text-sm text-gray-700">
        <span class="block font-medium text-gray-800">Email where we can send you important information about the study</span>
        <span class="mt-2 block"><span class="text-red-600">*</span> Email address</span>
        <input type="email" class={field} autocomplete="email" bind:value={email} />
      </label>

      <div class="mt-5">
        <span class="block font-medium text-gray-800">Password</span>
        <p class="mt-1 text-sm text-gray-600">
          Choose a password for your study account. You will use your email address and this
          password to sign in and submit your videos.
        </p>
        <label class="mt-2 block text-sm text-gray-700">
          <span class="block"><span class="text-red-600">*</span> Password (at least {minPasswordLength} characters)</span>
          <input type="password" class={field} autocomplete="new-password" bind:value={password} />
        </label>
        <label class="mt-3 block text-sm text-gray-700">
          <span class="block"><span class="text-red-600">*</span> Confirm password</span>
          <input
            type="password"
            class={field}
            autocomplete="new-password"
            bind:value={passwordConfirmation}
          />
        </label>
      </div>
    </fieldset>

    <fieldset class="mb-8">
      <p class="text-sm text-gray-600">
        Please provide the following demographic information which will be included in the research
        database along with your recordings.
      </p>

      <label class="mt-4 block text-sm text-gray-700">
        <span class="block"><span class="text-red-600">*</span> In what year were you born?</span>
        <select class={field} bind:value={yearOfBirth}>
          <option value="">Select…</option>
          {#each years as year}<option value={year}>{year}</option>{/each}
        </select>
      </label>

      <div class="mt-5">
        <span class="block text-sm text-gray-700"><span class="text-red-600">*</span> Sex assigned at birth</span>
        <div class="mt-2 space-y-1">
          {#each sexes as option}
            <label class="flex items-center gap-2 text-gray-800">
              <input type="radio" bind:group={sex} value={option} />
              {option}
            </label>
          {/each}
        </div>
      </div>
    </fieldset>

    <fieldset class="mb-8">
      <legend class="text-sm text-gray-700">
        <span class="text-red-600">*</span> Which languages do you speak, and at what level of fluency?
      </legend>
      <p class="mt-1 text-sm text-gray-500">
        Select all that apply. At least one of your languages must be
        &ldquo;{nativeProficiency}&rdquo;. Until you pick one, the other fluency levels stay
        unavailable.
      </p>

      <div class="mt-3 overflow-x-auto">
        <table class="w-full min-w-[36rem] border-collapse text-sm">
          <thead>
            <tr class="border-b border-gray-200 text-left text-gray-700">
              <th scope="col" class="py-2 pr-4 font-medium">Language</th>
              <th scope="col" class="py-2 pr-4 font-medium">Fluency Level</th>
              <th scope="col" class="py-2 font-medium">Did you speak this language during childhood?</th>
            </tr>
          </thead>
          <tbody>
            {#each languages as language}
              <tr class="border-b border-gray-100 align-top">
                <th scope="row" class="py-3 pr-4 text-left font-medium text-gray-800">
                  {language.label}
                </th>
                <td class="py-3 pr-4">
                  <select
                    class="border rounded w-full max-w-xs"
                    value={proficiency[language.key] ?? doNotSpeak}
                    onchange={(event) => setProficiency(language.key, event.currentTarget.value)}
                  >
                    {#each proficiencies as level}
                      <option value={level} disabled={levelDisabled(language.key, level)}>
                        {level}
                      </option>
                    {/each}
                  </select>
                </td>
                <td class="py-3">
                  {#if spoken(language.key)}
                    <div class="flex items-center gap-4">
                      {#each yesNoResponses as response}
                        <label class="flex items-center gap-2 text-gray-800">
                          <input
                            type="radio"
                            name={`childhood_${language.key}`}
                            value={response}
                            checked={childhood[language.key] === response}
                            onchange={() => (childhood[language.key] = response)}
                          />
                          {response}
                        </label>
                      {/each}
                    </div>
                  {:else}
                    <span class="text-gray-400">&mdash;</span>
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </fieldset>

    <label class="mb-8 block text-sm text-gray-700">
      <span class="block"><span class="text-red-600">*</span> Which of the above do you consider your primary language?</span>
      <select class={field + ' max-w-xs'} disabled={spokenLanguages.length === 0} bind:value={primaryLanguage}>
        <option value="">{spokenLanguages.length ? 'Select…' : 'Select a language above first…'}</option>
        {#each spokenLanguages as language}<option value={language.key}>{language.label}</option>{/each}
      </select>
    </label>

    <fieldset class="mb-8">
      <legend class="text-sm text-gray-700">
        <span class="text-red-600">*</span> Select which part(s) in which you plan to participate
        <span class="text-gray-500">(note that you can update your selections by contacting your recruiter)</span>
      </legend>
      <div class="mt-2 space-y-1">
        <label class="flex items-center gap-2 text-gray-800">
          <input type="checkbox" bind:checked={part1} />
          Part 1 (phone calls and videos)
        </label>
        <label class="flex items-center gap-2 text-gray-800">
          <input type="checkbox" bind:checked={part2} />
          Part 2 (in-person conversations and in-person phone calls)
        </label>
      </div>
    </fieldset>

    <fieldset class="mb-8">
      <legend class="text-sm text-gray-700">
        <span class="text-red-600">*</span> Have you participated in LINGOS?
      </legend>
      <div class="mt-2 flex items-center gap-6">
        {#each yesNoResponses as response}
          <label class="flex items-center gap-2 text-gray-800">
            <input type="radio" bind:group={lingos} value={response} />
            {response}
          </label>
        {/each}
      </div>
    </fieldset>

    <fieldset class="mb-8">
      <legend class="text-sm text-gray-700">
        <span class="text-red-600">*</span> Have you participated in any of the following collections?
      </legend>
      <p class="mt-2 rounded-md bg-gray-50 p-3 text-sm text-gray-700">{priorStudies.join(', ')}</p>
      <div class="mt-3 flex items-center gap-6">
        {#each yesNoResponses as response}
          <label class="flex items-center gap-2 text-gray-800">
            <input type="radio" bind:group={priorStudy} value={response} />
            {response}
          </label>
        {/each}
      </div>
    </fieldset>

    <div class="flex items-center gap-3">
      <button type="button" class={btnPrimary} onclick={toReview}>Review Information</button>
      <a href="/" class={btnSecondary}>Cancel Enrollment</a>
    </div>

  {:else if phase === 'review'}
    <h3 class="text-xl font-semibold text-gray-900">Please review your enrollment information</h3>
    <dl class="mt-4 border-t border-gray-100 divide-y divide-gray-100">
      {#snippet row(label, value)}
        <div class="flex items-start justify-between py-3 text-sm gap-6">
          <dt class="text-gray-500">{label}</dt>
          <dd class="font-medium text-gray-900 text-right">{value || '—'}</dd>
        </div>
      {/snippet}
      {@render row('First Name:', firstName)}
      {@render row('Last Name:', lastName)}
      {@render row('Email:', email)}
      {@render row('Sex Assigned at Birth:', sex)}
      {@render row('Year of Birth:', yearOfBirth)}
      <div class="flex items-start justify-between py-3 text-sm gap-6">
        <dt class="text-gray-500">Languages Spoken:</dt>
        <dd class="font-medium text-gray-900 text-right">
          {#if spokenLanguages.length}
            {#each spokenLanguages as language}
              <div>
                {language.label} — {proficiency[language.key]}
                <span class="text-gray-500">
                  (childhood: {childhood[language.key] || '—'})
                </span>
              </div>
            {/each}
          {:else}—{/if}
        </dd>
      </div>
      {@render row('Primary Language:', spokenLanguages.find((l) => l.key === primaryLanguage)?.label)}
      {@render row('Part(s) of Study in Which You Wish to Participate:', participatingParts.join(', '))}
      {@render row('Participated in LINGOS:', lingos)}
      {@render row('Participated in a Listed Collection:', priorStudy)}
    </dl>

    <p class="mt-4 text-gray-700">
      If any of this information is incorrect, click <strong>Edit Previous Information</strong>.
      Otherwise, review the consent statement below and click <strong>Complete Enrollment</strong>
      to proceed.
    </p>

    <h3 class="mt-8 text-xl font-semibold text-gray-900">Consent Statement</h3>
    <div class="mt-3 rounded-md bg-gray-50 ring-1 ring-gray-200 p-5 text-gray-700 leading-relaxed">
      The data you contribute will be included in a database that will be shared with individuals and
      organizations engaged in language-related research, education and technology development.
      Individuals and organizations that receive this material will first agree to use the database
      only for language-related research, education and technology development purposes. Names and
      emails collected for compensation, contact, and identification purposes are not distributed to individuals or
      organizations receiving the data. You can withdraw from the study or ask to have your data
      removed at any time by contacting study personnel.
    </div>

    <div class="mt-4 flex items-center gap-3">
      <button type="button" class={btnPrimary} onclick={complete} disabled={submitting}>
        {submitting ? 'Submitting…' : 'Complete Enrollment'}
      </button>
      <button type="button" class={btnSecondary} onclick={() => go('form')} disabled={submitting}>
        Edit Previous Information
      </button>
    </div>
  {/if}
</div>
